// The 🏥 chip (under the Threat chip) and THE HOSPITAL POP-UP: every patient by name, their condition (mild / serious / severe /
// terminal) and a Treat button each at the price that condition costs now — the price of neglect. Treating a TERMINAL patient fails
// (it is charged in full and the citizen dies) so that button asks to be confirmed. Opened from the chip, the 🏥 badge over the
// Hospital building (scene/hospitalBadge.js) or the rail card (Events.OpenHospital). Starving citizens are admitted one at a time
// (src/game/hospital.js); each has 48 hours.
import { Events, on } from './eventBus.js'
import * as hospital from '../game/hospital.js'
import { getWealth } from '../game/seasonManager.js'
import { GoonConfig, HospitalConfig, FeedConfig, MessageRailConfig, HomeCare, RealEstatePrices } from '../config/balance.js'
import { pushLog } from './hud.js'
import { postMessage, resolveMessage } from './messageRail.js'
import { formatCoins } from './format.js'
import { BUY_COINS_HTML, wireBuyCoins } from './buyCoinsLink.js'

const H = 3_600_000
function leftText(ms) {
  if (ms <= 0) return 'now'
  const h = Math.floor(ms / H), m = Math.floor((ms % H) / 60_000)
  return h ? `${h} h ${String(m).padStart(2, '0')} m` : `${m} min`
}
function agoText(ms) {
  const m = Math.round(ms / 60_000)
  if (m < 60) return `${m} min ago`
  const h = Math.floor(m / 60)
  return h < 48 ? `${h} h ago` : `${Math.floor(h / 24)} days ago`
}

// The price of one condition's treatment right now (home healthcare until a Hospital is built), and the line that says which it is.
const priceOf = (c) => Math.round(FeedConfig.costPerCitizen * HospitalConfig.healCostMult * c.costMult * hospital.careMult())
const baseOf = (c) => FeedConfig.costPerCitizen * HospitalConfig.healCostMult * c.costMult
const homeLine = () => `<b>🏠 Home healthcare.</b> With no Hospital built, every treatment costs ×${HomeCare.healthMult}. <b>Build a Hospital</b> (${formatCoins(RealEstatePrices.HOSPITAL)}) for more cost-effective care — ${HospitalConfig.conditions.filter((c) => !c.fatal).map((c) => `${c.label.toLowerCase()} ${formatCoins(baseOf(c))}`).join(' · ')} — and it heals the injured who wander past.`

export function mountHospitalChip() {
  const chip = document.createElement('button')
  chip.type = 'button'
  chip.className = 'hospital-chip'
  chip.setAttribute('aria-label', 'Citizens in the Hospital — open the Hospital')
  document.body.appendChild(chip)
  let overlay = null
  // The Hospital's call for help rides the message rail as a CHOICE: visit the Hospital, or later. Left unanswered it returns when the
  // rail is quiet until nobody is left in the Hospital. Re-posted only when its numbers change (or every 5 min).
  let lastSig = '', lastPostAt = 0
  const railCard = (n) => {
    const worst = hospital.worstCondition() ?? 'mild', soon = hospital.soonestLeftMs() ?? 0
    const label = HospitalConfig.conditions.find((c) => c.id === worst)?.label ?? worst
    const sig = `${n}|${worst}|${hospital.treatableCount()}|${hospital.hasHospital()}`
    if (sig === lastSig && Date.now() - lastPostAt < 300_000) return
    lastSig = sig; lastPostAt = Date.now()
    const price = (c) => formatCoins(priceOf(c))
    const home = !hospital.hasHospital()
    postMessage({
      key: 'hospital', kind: 'choice', type: 'HOSPITAL', icon: '🏥', title: `${n} in the Hospital`,
      text: `${n} starving ${n === 1 ? 'citizen lies' : 'citizens lie'} in the Hospital, earning nothing — the worst is ${label.toLowerCase()}. The first dies in ${leftText(soon)}.`,
      action: `Visit to treat each one by name${home ? ' (home healthcare)' : ''}. The longer they wait the worse and dearer: ${HospitalConfig.conditions.map((c) => `${c.label.toLowerCase()} ${price(c)}`).join(' → ')} — and a terminal patient cannot be saved.${home ? ` A Hospital would make care more cost-effective (×${HomeCare.healthMult} → base).` : ''}`,
      choices: [
        { label: 'Visit the Hospital', primary: true, onPick: () => open(), snoozeMs: MessageRailConfig.snoozeMs },
        { label: 'Later', snoozeMs: MessageRailConfig.snoozeMs },
      ],
    })
  }

  const refresh = () => {
    const n = hospital.count()
    chip.classList.toggle('on', n > 0)
    if (!n) { close(); lastSig = ''; resolveMessage('hospital'); return }
    railCard(n)
    const soon = hospital.soonestLeftMs()
    chip.classList.toggle('urgent', soon != null && soon < 6 * H)
    const worstId = hospital.worstCondition()
    chip.innerHTML = `🏥 <b>${n}</b> in hospital <i>${worstId && worstId !== 'mild' ? `worst: ${worstId} · ` : ''}heal: ${formatCoins(hospital.healCost())} · first death in ${leftText(soon ?? 0)}</i>`
    chip.title = `Starving citizens are carried to the Hospital. Healing them costs ×${HospitalConfig.healCostMult} the FEED rate (${formatCoins(priceOf(HospitalConfig.conditions[0]))} each while mild${hospital.hasHospital() ? '' : ' — home healthcare, ×' + HomeCare.healthMult + ' until you build a Hospital'}). A patient left ${Math.round(HospitalConfig.stayMs / H)} hours dies.`
    if (overlay) paint()
  }

  function close() { overlay?.remove(); overlay = null; confirmId = null }

  let lastHtml = ''
  let confirmId = null // the terminal patient whose (failing) treatment is awaiting a second press
  function paint() {
    const now = Date.now()
    const list = hospital.patients(now).sort((a, b) => a.at - b.at)
    const can = (c) => getWealth() >= c
    const treatable = hospital.treatableCount(now), allCost = hospital.healCost(now)
    const cond = HospitalConfig.conditions
    const price = (c) => formatCoins(priceOf(c))
    const html = `
      <div class="reg__confirm-card" style="border-color:#e04a3a; position: relative; max-width: 640px">
        <button type="button" class="msg-close" data-hospital-close aria-label="Close" title="Close">×</button>
        <div class="reg__confirm-title" style="color:#ff9d8a">🏥 The Hospital</div>
        <div class="reg__confirm-body">
          <strong>${list.length}</strong> ${list.length === 1 ? 'citizen' : 'citizens'} collapsed from hunger and ${list.length === 1 ? 'lies' : 'lie'} here, earning nothing. Each worsens the longer it waits — and a patient not treated within <strong>${Math.round(HospitalConfig.stayMs / H)} hours</strong> dies (the clock runs while you are away).
          <ul class="hospital-list">
            ${list.map((p) => { const bad = p.leftMs < 6 * H; const asking = confirmId === p.id; return `<li data-patient="${p.id}"><span class="who"><b>${p.name}</b><small>${GoonConfig[p.goonId]?.name ?? p.goonId} · admitted ${agoText(now - p.at)}</small></span><span class="cond cond--${p.condition}">${p.conditionLabel}</span><span class="left${bad ? ' soon' : ''}">${leftText(p.leftMs)}</span><button type="button" class="${p.fatal ? 'danger' : 'confirm'}" data-hospital-treat="${p.id}" ${can(p.cost) ? '' : 'disabled'}>${p.fatal ? (asking ? `Confirm — ${formatCoins(p.cost)} · ${p.name} will die` : `Treat — ${formatCoins(p.cost)} (cannot be saved)`) : `Treat — ${formatCoins(p.cost)}`}</button></li>` }).join('')}
          </ul>
          ${hospital.hasHospital() ? '' : `<span class="hospital-home">${homeLine()}</span>`}
          <span class="hospital-prices">Treatment costs: ${cond.map((c) => `<b class="cond cond--${c.id}">${c.label}</b> ${price(c)}`).join(' · ')}. A <b>terminal</b> patient cannot be saved — treating one is charged in full and the citizen dies; the cemetery takes them.</span>
        </div>
        <div class="reg__confirm-actions hospital-actions">
          <button type="button" class="confirm" data-hospital-heal ${treatable && can(allCost) ? '' : 'disabled'}>Treat all who can be saved${treatable ? ` (${treatable}) — ${formatCoins(allCost)}` : ''}</button>
          ${list.some((p) => !can(p.cost)) ? `<div class="hud__build-notice">You are short of coin for some of this. ${BUY_COINS_HTML}</div>` : ''}
          <button type="button" class="cancel" data-hospital-close>Not now</button>
        </div>
      </div>`
    // The treasury ticks every second (idle tally), and every tick calls paint(): rebuilding identical markup destroyed the button under
    // the pointer — it flashed on hover and a press could land on a node that no longer existed (the click "took a while"). Only a real change repaints.
    if (html === lastHtml) return
    lastHtml = html; overlay.innerHTML = html
    wireBuyCoins(overlay)
  }

  function open() {
    if (!hospital.count()) return
    close()
    lastHtml = ''
    overlay = document.createElement('div')
    overlay.className = 'reg__confirm hospital-popup'
    document.body.appendChild(overlay)
    paint()
    overlay.addEventListener('click', (e) => {
      if (e.target.closest('[data-hospital-close]') || e.target === overlay) { close(); return }
      const treat = e.target.closest('[data-hospital-treat]')
      if (treat) {
        const id = treat.dataset.hospitalTreat, p = hospital.patients().find((x) => x.id === id)
        if (!p) { paint(); return }
        if (p.fatal && confirmId !== id) { confirmId = id; paint(); return } // a terminal patient: ask once more
        confirmId = null
        const r = hospital.treat(id)
        if (r.ok && !r.died) pushLog(`🏥 ${r.name} was treated (${r.condition}) — ${formatCoins(r.cost)} — and is back at work.`)
        if (!hospital.count()) close(); else paint()
        return
      }
      if (e.target.closest('[data-hospital-heal]')) { if (hospital.healAll()) { if (!hospital.count()) close(); else paint() } else paint() }
    })
  }

  chip.addEventListener('click', open)
  on(Events.OpenHospital, open) // the 🏥 badge over the building, the rail card
  for (const ev of [Events.HospitalChange, Events.KingdomChange, Events.WealthChange, Events.AccountStatePulled]) on(ev, refresh)
  setInterval(refresh, 30_000) // the "time left" ticks down
  refresh()
  if (import.meta.env?.DEV) window.__hospitalChip = { open, close, isOpen: () => !!overlay }
}
